/**
 * The AI Agent as a studio: the four things it makes (a video, a scene, a series,
 * a cast) in a rail on the left, each opening to what the person has made in it,
 * and the chosen one's page beside it. The owner asked for it after seeing the modes
 * as tabs and a switch inside the idea box: "they need things to be as obvious as
 * possible, as simple as possible".
 *
 * A layout route (App.tsx), so the rail stays put while the pages change. On a
 * phone a left rail does not fit and a bar along the bottom would sit on the pinned
 * Generate button, so there the four modes are the row of buttons each page already
 * opens with (AIAgentTabs). Shown where the studio is on (components/scenes/
 * useSceneStudio.js); otherwise the pages render as they always have.
 */
import { Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, ChevronDown, Clapperboard, Loader2, Plus, Sparkles, UserRound, Users } from 'lucide-react';
import { api } from '../../api/client.js';
import { track } from '../../analytics/track';
import { useAuth } from '../../context/AuthContext';
import { useLocale } from '../../i18n/LocaleContext';
import { cn } from '@/lib/utils';
import useSceneStudio from '../scenes/useSceneStudio';
import useMyScenes from '../scenes/useMyScenes';
import useCharacters from '../characters/useCharacters';
import { useSidebar } from '../AppShell/SidebarContext';

export const STUDIO_MODES = [
  { key: 'video', to: '/ai-agent', Icon: Sparkles, create: '/ai-agent' },
  { key: 'scenes', to: '/ai-agent/scenes', Icon: Clapperboard, create: '/ai-agent/scenes' },
  { key: 'series', to: '/ai-agent/series', Icon: CalendarClock, create: '/ai-agent/series/new' },
  { key: 'characters', to: '/ai-agent/characters', Icon: Users, create: '/ai-agent/characters' },
];

/** Which mode a page of the AI Agent belongs to. */
export function studioModeOf(pathname) {
  if (pathname.startsWith('/ai-agent/scenes')) return 'scenes';
  if (pathname.startsWith('/ai-agent/series')) return 'series';
  if (pathname.startsWith('/ai-agent/characters')) return 'characters';
  return 'video';
}

const SHOWN = 5;

/** What the person has made in one mode: a few of the latest, each a link. */
export function ModeItems({ mode }) {
  const { t } = useLocale();
  const videos = useQuery({
    queryKey: ['studio-rail', 'videos'],
    queryFn: () => api.getProjects({ page: 1, pageSize: 12, projectType: 'ai_agent' }),
    enabled: mode === 'video',
    staleTime: 30 * 1000,
  });
  const scenes = useMyScenes({ enabled: mode === 'scenes' });
  const series = useQuery({ queryKey: ['series'], queryFn: api.listSeries, enabled: mode === 'series', staleTime: 30 * 1000 });
  const cast = useCharacters({ enabled: mode === 'characters' });

  let loading = false;
  let items = [];
  if (mode === 'video') {
    loading = videos.isLoading;
    items = (videos.data?.projects || [])
      .filter((project) => project?.ai_agent_json?.kind !== 'scene' && String(project.status).toLowerCase() !== 'deleted')
      .map((project) => ({ id: project.id, to: `/ai-agent/${project.id}`, name: project.title, busy: String(project.status).toLowerCase() === 'processing' }));
  } else if (mode === 'scenes') {
    loading = scenes.isLoading;
    items = (scenes.data || []).filter((scene) => scene.status !== 'failed')
      .map((scene) => ({ id: scene.id, to: `/ai-agent/${scene.id}`, name: scene.title, picture: scene.poster_url, busy: scene.status === 'processing' }));
  } else if (mode === 'series') {
    loading = series.isLoading;
    items = (series.data || []).map((item) => ({ id: item.id, to: `/ai-agent/series/${item.id}`, name: item.name }));
  } else {
    loading = cast.loading;
    items = cast.characters.map((character) => ({ id: character.id, to: '/ai-agent/characters', name: character.name, picture: character.portrait_url, round: true }));
  }

  if (loading) return <p className="px-3 py-2 text-xs text-muted-foreground"><Loader2 className="inline h-3.5 w-3.5 animate-spin" /></p>;
  if (!items.length) return <p className="px-3 py-1.5 text-xs text-muted-foreground" data-testid="rail-empty">{t(`studio.empty.${mode}`)}</p>;
  const shown = items.slice(0, SHOWN);
  return (
    <ul className="grid grid-cols-1 gap-0.5" data-testid={`rail-items-${mode}`}>
      {shown.map((item) => (
        <li key={item.id}>
          <Link
            to={item.to}
            onClick={() => track('studio_rail_item', { mode })}
            className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {mode === 'scenes' || mode === 'characters' ? (
              item.picture
                ? <img src={item.picture} alt="" loading="lazy" className={cn('h-7 w-7 shrink-0 object-cover', item.round ? 'rounded-full object-top' : 'rounded')} />
                : <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center bg-muted', item.round ? 'rounded-full' : 'rounded')}><UserRound className="h-3.5 w-3.5" /></span>
            ) : null}
            <span className="min-w-0 flex-1 truncate" dir="auto">{item.name}</span>
            {item.busy && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />}
          </Link>
        </li>
      ))}
      {items.length > shown.length && (
        <li>
          <Link to={STUDIO_MODES.find((item) => item.key === mode).to} className="block rounded-md px-3 py-1.5 text-xs font-medium text-foreground/80 hover:bg-muted hover:text-foreground">
            {t('studio.seeAll')}
          </Link>
        </li>
      )}
    </ul>
  );
}

export function StudioRail({ className }) {
  const { t } = useLocale();
  const { pathname } = useLocation();
  const { scenesOn } = useSceneStudio();
  // With no provider around it (a page mounted on its own) there is nobody to wait for
  const auth = useAuth();
  const isAuthenticated = auth ? Boolean(auth.isAuthenticated) : true;
  const active = studioModeOf(pathname);
  // The mode you are in is open; any other can be opened to look inside it
  const [open, setOpen] = useState(active);
  useEffect(() => { setOpen(active); }, [active]);
  const modes = STUDIO_MODES.filter((mode) => mode.key !== 'scenes' || scenesOn);

  return (
    <nav aria-label={t('studio.label')} data-testid="studio-rail" className={cn('flex-col gap-1 overflow-x-hidden border-e bg-card/30 p-3', className)}>
      <p className="px-3 pb-2 pt-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{t('studio.label')}</p>
      {modes.map(({ key, to, Icon, create }) => {
        const current = active === key;
        const expanded = open === key;
        return (
          <div key={key} className={cn('rounded-xl border transition-colors', current ? 'border-primary/40 bg-primary/5' : 'border-transparent')} data-testid={`rail-mode-${key}`}>
            <div className="flex items-stretch">
              <NavLink
                to={to}
                end={key === 'video'}
                onClick={() => track('studio_rail_mode', { mode: key })}
                aria-current={current ? 'page' : undefined}
                className={cn('flex min-w-0 flex-1 items-start gap-3 rounded-xl px-3 py-2.5 transition-colors', current ? 'text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground')}
              >
                <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', current && 'text-primary')} aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{t(`studio.modes.${key}.name`)}</span>
                  <span className="block text-xs text-muted-foreground">{t(`studio.modes.${key}.what`, { n: 8 })}</span>
                </span>
              </NavLink>
              <button
                type="button"
                onClick={() => setOpen(expanded ? null : key)}
                aria-expanded={expanded}
                aria-label={t(expanded ? 'studio.close' : 'studio.open', { name: t(`studio.modes.${key}.name`) })}
                className="flex w-9 shrink-0 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground"
                data-testid={`rail-toggle-${key}`}
              >
                <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} />
              </button>
            </div>
            {expanded && (
              <div className="grid grid-cols-1 gap-1 pb-2">
                <Link
                  to={create}
                  // Each press is news to the page, even when it is the page already open: there
                  // "New video" did nothing, and one new person pressed it 14 times in 18 seconds
                  state={{ newItem: Date.now() }}
                  onClick={() => track('studio_rail_new', { mode: key })}
                  className="mx-2 flex items-center gap-2 rounded-md border border-dashed px-2.5 py-1.5 text-sm text-foreground/90 transition-colors hover:border-primary/60 hover:bg-primary/5"
                  data-testid={`rail-new-${key}`}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {t(`studio.modes.${key}.new`)}
                </Link>
                {isAuthenticated && <ModeItems mode={key} />}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export default function StudioShell() {
  const { studioOn } = useSceneStudio();
  // The app's sidebar (components/AppShell) carries the modes and what was made in them
  const { hostsStudioRail } = useSidebar();
  // Not (yet) a studio for this person: the pages as they always were
  if (!studioOn || hostsStudioRail) return <Outlet />;
  return (
    <div className="lg:flex" data-testid="studio-shell">
      {/* Under the 64 px site nav, and as tall as what is left of the window */}
      <StudioRail className="sticky top-16 hidden h-[calc(100vh-4rem)] w-64 shrink-0 overflow-y-auto lg:flex" />
      <div className="min-w-0 flex-1">
        {/* A page still loading leaves the rail where it is */}
        <Suspense fallback={<div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  );
}
